import SiteInertiaController from '@/actions/App/Http/Controllers/SiteInertiaController';
import { get } from '@/lib/axios';
import { type AvailableVoucher } from '@/types/voucher';
import { usePage } from '@inertiajs/react';
import { useEffect, useState } from 'react';

/**
 * Routes where the floating dock must stay out of the way: checkout has its own
 * voucher input, and the search page renders contextual site/rate vouchers
 * in-page — a second, generic surface there would duplicate codes on screen.
 */
const SUPPRESSED_PATHS = ['/checkout', '/dich-vu'];

interface UseAvailableVouchersReturn {
  vouchers: AvailableVoucher[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAvailableVouchers(): UseAvailableVouchersReturn {
  const [vouchers, setVouchers] = useState<AvailableVoucher[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pathname = usePage().url;

  const fetchVouchers = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const res = await get(SiteInertiaController.availableVoucher().url);

      if (res.success && Array.isArray(res.data)) {
        setVouchers(res.data);
      } else {
        setError('Failed to load vouchers');
      }
    } catch (err) {
      console.error('Error fetching vouchers:', err);
      setError('An error occurred while loading vouchers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // `usePage().url` carries the query string, and /dich-vu always has one.
    // Never fetching leaves `isLoading` true forever, which is exactly how
    // StickyVoucher stays hidden — the same mechanism /checkout relies on.
    const path = pathname.split('?')[0];
    if (SUPPRESSED_PATHS.includes(path)) {
      return;
    }
    if (vouchers.length > 0) {
      return;
    }
    fetchVouchers();

    // const interval = setInterval(fetchVouchers, 600000);

    // return () => clearInterval(interval);
  }, []);

  return {
    vouchers,
    isLoading,
    error,
    refetch: fetchVouchers,
  };
}
